import React, {useContext, useEffect, useMemo, useState} from "react";
import {Fade} from "react-reveal";
import emoji from "react-easy-emoji";
import "./Greeting.scss";
import landingPerson from "../../assets/lottie/landingPerson";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";
import SocialMedia from "../../components/socialMedia/SocialMedia";
import Button from "../../components/button/Button";
import {illustration, greeting, resumeSection} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

function RotatingTagline({phrases, isDark}) {
  const safePhrases = useMemo(
    () => (Array.isArray(phrases) && phrases.length > 0 ? phrases : []),
    [phrases]
  );
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (safePhrases.length <= 1) return;

    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % safePhrases.length);
        setVisible(true);
      }, 220);
    }, 2600);

    return () => clearInterval(interval);
  }, [safePhrases]);

  if (safePhrases.length === 0) return null;

  return (
    <div className="greeting-tagline" aria-label="Professional tagline">
      <span
        className={
          (isDark ? "dark-mode " : "") +
          (visible ? "tagline-text" : "tagline-text tagline-text--hidden")
        }
      >
        {safePhrases[index]}
      </span>
    </div>
  );
}

function extractFilename(p) {
  const s = typeof p === "string" ? p.trim() : "";
  if (!s) return "";
  return decodeURIComponent(
    s.split("?")[0].split("#")[0].replace(/\\/g, "/").split("/").pop() || ""
  );
}

function resolveResumeFromAssetsImages(resumeFilePath) {
  const raw = typeof resumeFilePath === "string" ? resumeFilePath.trim() : "";
  if (!raw) return {href: "", filename: ""};

  const filename = extractFilename(raw);
  if (!filename) return {href: "", filename: ""};

  // Load PDF from src/assets/images via webpack context
  try {
    const pdfs = require.context("../../assets/images", false, /\.pdf$/i);
    const href = pdfs(`./${filename}`);
    return {href, filename};
  } catch (_) {
    // If the PDF isn't actually in src/assets/images in this build, fail gracefully
    return {href: "", filename: ""};
  }
}

function resolveImageFromAssetsImages(imagePathOrModule) {
  // Supports:
  // - built `require(...)` output (string URL)
  // - preview/CMS string like "/assets/images/logo.png" or "logo.png"
  // - svg loaders that return { default: "..." }
  if (!imagePathOrModule) return "";

  // CRA sometimes returns an object for some assets (e.g. SVG)
  if (typeof imagePathOrModule === "object" && imagePathOrModule.default) {
    return String(imagePathOrModule.default);
  }

  if (typeof imagePathOrModule !== "string") return "";

  const raw = imagePathOrModule.trim();
  if (!raw) return "";

  // Already a usable URL (e.g. webpack output, data URI, http)
  if (
    raw.startsWith("data:") ||
    raw.startsWith("blob:") ||
    raw.startsWith("http://") ||
    raw.startsWith("https://")
  ) {
    return raw;
  }

  const filename = extractFilename(raw);
  if (!filename) return raw;

  // Try to load from src/assets/images so it works in both build + CMS preview.
  try {
    const images = require.context(
      "../../assets/images",
      false,
      /\.(png|jpe?g|gif|webp|svg)$/i
    );
    return images(`./${filename}`);
  } catch (_) {
    // As a fallback, allow absolute paths like /assets/images/...
    return raw;
  }
}

export default function Greeting() {
  const {isDark} = useContext(StyleContext);

  if (!greeting.displayGreeting) return null;

  const {href: resumeHref, filename} = resolveResumeFromAssetsImages(
    resumeSection?.resumeFile
  );

  const hasResume = Boolean(resumeHref);

  const greetingLogoSrc =
    resolveImageFromAssetsImages(greeting.logoIcon) ||
    require("../../assets/images/tallguylogo.png");

  return (
    <Fade bottom duration={1000} distance="40px">
      <div className="greet-main" id="greeting">
        <div className="greeting-main">
          <div className="greeting-text-div">
            <div>
              <h1 className={isDark ? "dark-mode greeting-text" : "greeting-text"}>
                {" "}
                {greeting.title} <span className="wave-emoji">{emoji("👋")}</span>
              </h1>

              <RotatingTagline phrases={greeting.taglines} isDark={isDark} />

              <p className={isDark ? "dark-mode greeting-text-p" : "greeting-text-p subTitle"}>
                {greeting.subTitle}
              </p>

              <div id="resume" className="empty-div"></div>
              <SocialMedia />

              <div className="button-greeting-div">
                <Button text="View Portfolio" href="#portfolio" />
                <Button text="Contact" href="#contact" />

                {/* ✅ Invisible if not set */}
                {hasResume ? (
                  <Button
                    text="Download Resume"
                    href={resumeHref}
                    download={filename || "Resume.pdf"}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="greeting-image-div">
            {illustration.animated ? (
              <DisplayLottie animationData={landingPerson} />
            ) : (
              <img
                alt="man sitting on table"
                src={greetingLogoSrc}
                className="greeting-hero-image"
              />
            )}
          </div>
        </div>
      </div>
    </Fade>
  );
}
